import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, API } from "../lib/api";
import { ArrowRight, Calculator, Package, Layers, Footprints, Trees } from "lucide-react";

import imgStraight from "../assets/products/straight-ramp.webp";
import imgWing from "../assets/products/wing-ramp.webp";
import imgInfill from "../assets/products/infill.webp";
import imgStep from "../assets/products/step.webp";
import imgTimberStraight from "../assets/products/timber/straight.webp";
import imgTimberWing from "../assets/products/timber/wing.webp";
import imgTimberInfill from "../assets/products/timber/infill.webp";
import imgTimberStep from "../assets/products/timber/step.webp";

const RUBBER = [
  { id: "straight-ramp", key: "straight_ramp", route: "straight-ramp", title: "Straight Rubber Ramp",
    desc: "For standard doorway/threshold ramps with no side wings.", icon: Calculator, defaultImg: imgStraight, accent: "#254391" },
  { id: "wing-ramp",     key: "wing_ramp",     route: "wing-ramp",     title: "Rubber Ramp With Wings",
    desc: "Custom ramp transitions requiring left/right side wing extensions.", icon: Layers, defaultImg: imgWing, accent: "#fda500" },
  { id: "infill",        key: "infill",        route: "infill",        title: "Custom Rubber Infill",
    desc: "Bespoke rubber infill panels for gaps, level changes and platforms.", icon: Package, defaultImg: imgInfill, accent: "#254391" },
  { id: "step",          key: "step",          route: "step",          title: "Rubber Step / Platform",
    desc: "Custom rubber steps, raised pads, and platform access solutions.", icon: Footprints, defaultImg: imgStep, accent: "#fda500" },
];

const TIMBER = [
  { id: "timber-ramp",     key: "timber_ramp",     route: "wing-ramp?material=timber&pt=timber_ramp",
    title: "Timber Ramp", desc: "Custom timber access ramps with non-slip surface.", icon: Trees, defaultImg: imgTimberStraight, accent: "#7c5530" },
  { id: "timber-step",     key: "timber_step",     route: "step?material=timber&pt=timber_step",
    title: "Timber Platform / Step", desc: "Solid timber landings and stepped platforms.", icon: Footprints, defaultImg: imgTimberStep, accent: "#7c5530" },
  { id: "timber-infill",   key: "timber_infill",   route: "infill?material=timber&pt=timber_infill",
    title: "Timber Infill", desc: "Bespoke timber infill solutions for thresholds and level changes.", icon: Package, defaultImg: imgTimberInfill, accent: "#7c5530" },
  { id: "timber-custom",   key: "timber_custom",   route: "wing-ramp?material=timber&pt=timber_custom",
    title: "Timber Ramp With Wings", desc: "Custom timber ramps with side wings for non-square thresholds.", icon: Layers, defaultImg: imgTimberWing, accent: "#7c5530" },
];

const BACKEND = (API || "").replace(/\/api$/, "");
const resolveUrl = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

export default function ProductSelector() {
  const { user } = useAuth();
  const [custom, setCustom] = useState({});
  const [params] = useSearchParams();
  const isContinue = params.get("continue") === "1";
  const [pendingCount, setPendingCount] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("eas_pending_quote") || "{}").items?.length || 0; }
    catch { return 0; }
  });

  useEffect(() => {
    // Re-read on focus / mount in case sessionStorage was just written
    const sync = () => {
      try { setPendingCount(JSON.parse(sessionStorage.getItem("eas_pending_quote") || "{}").items?.length || 0); }
      catch { setPendingCount(0); }
    };
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => { window.removeEventListener("storage", sync); window.removeEventListener("focus", sync); };
  }, []);

  useEffect(() => {
    api.get("/product-images")
      .then((r) => setCustom(r.data || {}))
      .catch(() => setCustom({}));
  }, []);

  const renderCards = (list) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6">
      {list.map((p) => {
        const Icon = p.icon;
        const customUrl = custom[p.key];
        const src = customUrl ? resolveUrl(customUrl) : p.defaultImg;
        return (
          <Link
            key={p.id}
            to={`/quote-portal/calculators/${p.route}`}
            data-testid={`product-${p.id}`}
            className="group bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-[0_8px_24px_rgba(10,17,40,0.08)] hover:border-[#254391]/40 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden flex flex-col"
          >
            <div className="aspect-[16/10] bg-slate-50 relative overflow-hidden border-b border-slate-100">
              <img src={src} alt={p.title} data-testid={`product-img-${p.id}`}
                className="w-full h-full object-contain p-5 group-hover:scale-105 transition-transform duration-500"
                onError={(e) => { if (e.currentTarget.src !== p.defaultImg) e.currentTarget.src = p.defaultImg; }} />
              <div className="absolute top-3 right-3 w-9 h-9 rounded-md flex items-center justify-center shadow-md" style={{ background: p.accent }}>
                <Icon size={16} className="text-white" />
              </div>
            </div>
            <div className="p-5 flex flex-col flex-1">
              <h3 className="text-base md:text-lg font-bold text-[#0A1128] tracking-tight mb-1.5">{p.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-5 flex-1">{p.desc}</p>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Open Calculator</span>
                <span className="px-3.5 py-1.5 rounded-md font-bold text-xs bg-[#254391] text-white flex items-center gap-1.5 transition-colors group-hover:bg-[#1E3675] group-hover:gap-2">
                  Start Quote <ArrowRight size={12} />
                </span>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="product-selector-page">
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 md:p-8 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#fda500]" />
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-1.5">
            {isContinue ? `Adding item ${pendingCount + 1} to this quote` : `Welcome back, ${user?.name?.split(" ")[0]}`}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0A1128]">
            Select Product Type to Quote
          </h1>
          <p className="text-sm text-slate-500 mt-2">Built for Access. Made with Care.</p>
        </div>
      </div>

      <div>
        <div className="flex items-end justify-between gap-4 mb-4 pb-3 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#254391] font-bold mb-1">Category 01</div>
            <h2 className="text-xl md:text-2xl font-bold tracking-tight text-[#0A1128]">Rubber Ramps</h2>
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 hidden sm:block">100% Australian recycled rubber</div>
        </div>
        {renderCards(RUBBER)}
      </div>

      <div>
        <div className="flex items-end justify-between gap-4 mb-4 pb-3 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-1">Category 02</div>
            <h2 className="text-xl md:text-2xl font-bold tracking-tight text-[#0A1128]">Timber Ramps</h2>
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 hidden sm:block">Hardwood, non-slip finish · pricing uses rubber model until calibrated</div>
        </div>
        {renderCards(TIMBER)}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#fda500] font-bold mb-1">Recent Activity</div>
          <div className="text-sm text-gray-700">View saved quotes and pricing history</div>
        </div>
        <div className="flex gap-2">
          <Link to="/quote-portal/quotes" className="px-3.5 py-2 text-sm border border-gray-300 hover:border-[#254391] hover:text-[#254391] rounded-md font-medium">View Quotes</Link>
          {(user?.role === "super_admin" || user?.role === "admin") && (
            <Link to="/quote-portal/admin/pricing" className="px-3.5 py-2 text-sm bg-[#254391] hover:bg-[#1E3675] text-white rounded-md font-medium">Manage Pricing</Link>
          )}
        </div>
      </div>
    </div>
  );
}
